import { observer, useLocalObservable } from 'mobx-react';
import { useMemo, useRef } from 'react';
import { FIELDS_TYPES } from '../../utils/constants/fields';
import InputWrapper from './components/InputWrapper';
import { getTooltipId } from '../../utils/utils';
import { ICON_BUTTON_ICONS } from '../buttons/IconButton';
import { Tooltip } from 'react-tooltip';

const Input = observer(
  ({
    field,
    value = '',
    setFieldValue,
    showOnSubmitErrorState = false,
    messages = [],
    disabled = false,
    hideErrorMessages = false,
    hideRequiredLabelSymbol = false,
    hideLabel = false,
    inputWrap = { enable: false, className: '' },
    wrapClassname = '',
    onAfterValueChange = () => {},
    onFocus = () => {},
    onBlur = () => {},
    labelInfo = '',
    labelInfoTooltipClassname = ''
  }) => {
    const state = useLocalObservable(() => ({
      inputType: field.TYPE === FIELDS_TYPES.TYPE_NUMBER ? FIELDS_TYPES.TYPE_TEXT : field.TYPE,
      isVisible: !field.VISIBILITY_TOGGLE,
      toggleVisibility: () => {
        [state.isVisible, state.inputType] = [
          !state.isVisible,
          state.inputType === field.TYPE ? FIELDS_TYPES.TYPE_TEXT : field.TYPE
        ];
      },
      labelInfoTooltipId: getTooltipId()
    }));

    // Provide a way to strongly discourage browser autofill when needed
    const randomizedName = useMemo(() =>
      `af-${field?.ID || field?.NAME || 'fld'}-${Math.random().toString(36).slice(2)}`, []
    )
    const randomizedNameRef = useRef(randomizedName);

    const props = useMemo(() => {
      const [inputProps, labelProps] = [{}, {}];

      inputProps.className = field.INPUT_CLASSNAME || 'form-control';

      if (field.VISIBILITY_TOGGLE) {
        inputProps.className += ' showable-password';
      }

      if (typeof field.MINLENGTH === 'number') {
        inputProps.minLength = `${field.MINLENGTH}`;
      }

      if (typeof field.MAXLENGTH === 'number') {
        inputProps.maxLength = `${field.MAXLENGTH}`;
      }

      if (field.TYPE === FIELDS_TYPES.TYPE_NUMBER) {
        inputProps.inputMode = 'numeric';

        if (!field.INTEGER) {
          inputProps.inputMode = 'decimal';
        }
      }

      if (field.LABEL_CLASSNAME) {
        labelProps.className = field.LABEL_CLASSNAME;
      }

      return { inputProps, labelProps };
    }, [field]);

    const { validationState, onSubmitErrorMessages } = useMemo(() => {
      let validationState = '';
      const shouldShowMessages = showOnSubmitErrorState || field.SHOW_MESSAGES_ON_INPUT_VALUE;
      const onSubmitErrorMessages = shouldShowMessages
        ? messages.filter((m) => m.field === field.NAME && !m.isValid)
        : [];
      if (shouldShowMessages && onSubmitErrorMessages.length) {
        validationState = 'error';
      }

      return { validationState, onSubmitErrorMessages };
    }, [showOnSubmitErrorState, messages, field.SHOW_MESSAGES_ON_INPUT_VALUE]);

    const withWrap = inputWrap.enable;

    const view = (
      <div
        className={wrapClassname || field.WRAP_CLASSNAME || 'form-group'}
        {...{ 'validation-state': validationState }}>
        {!hideLabel && !!field.LABEL && (
          <label htmlFor={field.ID} {...props.labelProps}>
            <div className="label-inner-wrapper">
              {(!!field.LABEL_INFO || !!labelInfo) && (
                <div className="info-icon-wrap" id={state.labelInfoTooltipId}>
                  <img src={ICON_BUTTON_ICONS.INFO_CIRCLE} />
                  <Tooltip
                    anchorSelect={`#${state.labelInfoTooltipId}`}
                    multiline={true}
                    variant="dark"
                    opacity={1}
                    place="bottom-center"
                    content={field.LABEL_INFO || labelInfo}
                    className={
                      labelInfoTooltipClassname ||
                      field.LABEL_INFO_TOOLTIP_CLASSNAME ||
                      'default-label-info-tooltip-classname'
                    }
                  />
                </div>
              )}
              <div>
                {field.LABEL}
                {field.REQUIRED &&
                  !hideRequiredLabelSymbol &&
                  !field.HIDE_REQUIRED_LABEL_SYMBOL && <font className="red-asterisk">*</font>}
                {!!field.LABEL_DESCRIPTION && <span>{field.LABEL_DESCRIPTION}</span>}
              </div>
            </div>
          </label>
        )}
        <div className="input-error-group">
          <div className="input-container">
            <input
              type={state.inputType}
              id={field.ID}
              name={field.DISABLE_AUTOFILL ? randomizedNameRef.current : field.ID}
              placeholder={field.PLACEHOLDER}
              required={field.REQUIRED}
              value={value}
              disabled={disabled}
              autoComplete={field.DISABLE_AUTOFILL ? 'new-password' : field.INPUT_AUTOCOMPLETE || 'on'}
              autoCorrect="off"
              autoCapitalize="none"
              spellCheck={false}
              aria-autocomplete="none"
              onFocus={onFocus}
              onBlur={onBlur}
              onChange={(e) => {
                const newValue =
                  typeof field.FORMAT === 'function'
                    ? field.FORMAT(e.target.value)
                    : field.TYPE === FIELDS_TYPES.TYPE_NUMBER
                      ? ((newValue, oldValue, field) => {
                          if (
                            (newValue.startsWith('-') &&
                              (typeof field.MIN !== 'number' || field.MIN < 0)) ||
                            newValue === ''
                          ) {
                            return newValue;
                          }

                          let num = newValue.replace(',', '.');
                          if (
                            newValue.startsWith('-') &&
                            typeof field.MIN === 'number' &&
                            field.MIN >= 0
                          ) {
                            num = newValue.replace(/-/g, '');
                          }

                          if (field.INTEGER) {
                            if (!/^-?\d+$/.test(num)) {
                              return oldValue;
                            }
                          } else {
                            if (!/^-?\d+\.?(\d{1,2})?$/.test(num)) {
                              return oldValue;
                            }
                          }

                          let numArray = num.split('');

                          while (
                            (numArray[0] === '0' &&
                              typeof numArray[1] === 'string' &&
                              !['.', ','].includes(numArray[1])) ||
                            (numArray[0] === '-' &&
                              numArray[1] === '0' &&
                              typeof numArray[2] === 'string' &&
                              !['.', ','].includes(numArray[2]))
                          ) {
                            numArray.splice(1, 1);
                          }

                          num = numArray.join('');

                          return num + '';
                        })(e.target.value, value, field)
                      : e.target.value;
                setFieldValue(field, newValue);
                onAfterValueChange(newValue);
              }}
              {...props.inputProps}
            />
            {field.VISIBILITY_TOGGLE && (
              <div
                className={`show-password-btn password-${state.isVisible ? 'visible' : 'hidden'}`}
                onClick={state.toggleVisibility}
              />
            )}
          </div>
          {!hideErrorMessages &&
            onSubmitErrorMessages
              .filter(({ msg }) => msg.length)
              .map(({ msg }, idx) => (
                <div
                  key={idx}
                  className={
                    field.ON_SUBMIT_ERROR_STATE_ERROR_MESSAGE_CLASSNAME || 'error-msg-input'
                  }>
                  {msg}
                </div>
              ))}
        </div>
      </div>
    );

    return withWrap ? (
      <InputWrapper className={inputWrap.className || ''}>{view}</InputWrapper>
    ) : (
      view
    );
  }
);

export default Input;
